import * as XLSX from "xlsx";

export interface ExcelColumn {
  header: string;
  key: string;
  width?: number;
}

export interface ExcelData {
  sheetName: string;
  columns: ExcelColumn[];
  data: any[];
  fileName: string;
}

/**
 * Exports data to Excel file
 * @param excelData - Structured data with columns and rows
 */
export const exportToExcel = (excelData: ExcelData): void => {
  try {
    const { sheetName, columns, data, fileName } = excelData;

    // Transform data to match column keys
    const transformedData = data.map((row) => {
      const transformedRow: any = {};
      columns.forEach((col) => {
        transformedRow[col.header] = row[col.key] ?? "";
      });
      return transformedRow;
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(transformedData);

    // Set column widths
    const columnWidths = columns.map((col) => ({
      wch: col.width || 20,
    }));
    worksheet["!cols"] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate file name with timestamp
    const timestamp = new Date().toISOString().slice(0, 10);
    const fullFileName = `${fileName}_${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, fullFileName);
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    throw new Error("Failed to export data to Excel");
  }
};

/**
 * Format number as currency
 */
export const formatCurrency = (value: number): string => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(2)}K`;
  return `$${value.toFixed(2)}`;
};

/**
 * Format number with commas
 */
export const formatNumber = (value: number): string => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
